import React, { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const ImageCarousel = ({ title, images = [], onPreviewImage }) => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    setIndex(0);
  }, [images]);

  if (!images.length) {
    return (
      <Box>
        <Typography fontSize={14} fontWeight={600} mb={1}>
          {title}
        </Typography>
        <Typography fontSize={12} color="text.secondary">
          No images found
        </Typography>
      </Box>
    );
  }

  const currentImage = images[index];

  return (
    <Box>
      <Typography fontSize={14} fontWeight={600} mb={1}>
        {title}
      </Typography>

      <Box
        sx={{
          position: "relative",
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid #e0e0e0",
          backgroundColor: "#fafafa",
        }}
      >
        <Box
          component="img"
          src={currentImage?.url}
          alt={title}
          onClick={() => onPreviewImage?.(currentImage?.url)}
          sx={{
            width: "100%",
            height: 260,
            objectFit: "contain",
            display: "block",
            cursor: "pointer",
          }}
        />

        {images.length > 1 && (
          <>
            <IconButton
              size="small"
              onClick={() =>
                setIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
              }
              sx={{
                position: "absolute",
                top: "50%",
                left: 8,
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ChevronLeft size={16} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setIndex((prev) => (prev + 1) % images.length)}
              sx={{
                position: "absolute",
                top: "50%",
                right: 8,
                transform: "translateY(-50%)",
                bgcolor: "rgba(0,0,0,0.5)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.7)" },
              }}
            >
              <ChevronRight size={16} />
            </IconButton>
          </>
        )}
      </Box>

      <Typography fontSize={11} color="text.secondary" mt={0.5}>
        {index + 1}/{images.length}
        {currentImage?.uploadedAt
          ? ` - ${new Date(currentImage.uploadedAt).toLocaleString()}`
          : ""}
      </Typography>
    </Box>
  );
};

const CommentsSection = ({ title, comments = [] }) => (
  <Box>
    <Typography fontSize={14} fontWeight={600} mb={1}>
      {title}
    </Typography>
    {comments.length ? (
      <Stack gap={1}>
        {comments.map((comment, idx) => (
          <Box
            key={comment?._id || idx}
            sx={{
              p: 1,
              border: "1px solid #ececec",
              borderRadius: 1,
              bgcolor: "#fafafa",
            }}
          >
            <Typography fontSize={13}>{comment?.text || "-"}</Typography>
            <Typography fontSize={11} color="text.secondary" mt={0.25}>
              {comment?.createdAt
                ? new Date(comment.createdAt).toLocaleString()
                : "No date"}
            </Typography>
          </Box>
        ))}
      </Stack>
    ) : (
      <Typography fontSize={12} color="text.secondary">
        No comments found
      </Typography>
    )}
  </Box>
);

const ViewSubmittedImagesDialog = ({
  open,
  onClose,
  workers = [],
  selectedWorkerId,
  submittedData,
  loading,
  onWorkerClick,
}) => {
  const [previewImage, setPreviewImage] = useState("");

  const workerLabel = useMemo(() => {
    const selectedWorker = workers.find(
      (worker) => worker?._id === selectedWorkerId,
    );
    if (!selectedWorker) return "Select a worker to view images and comments";
    return `${selectedWorker.firstName || ""} ${selectedWorker.lastName || ""}`.trim();
  }, [workers, selectedWorkerId]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
          }}
        >
          <Typography variant="h6" fontWeight={600}>
            Submitted Images
          </Typography>
          <IconButton onClick={onClose} size="small">
            <X size={20} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} gap={2}>
            <Box
              sx={{
                width: { xs: "100%", md: 280 },
                border: "1px solid #ececec",
                borderRadius: 2,
                p: 1,
              }}
            >
              <Typography fontSize={14} fontWeight={600} mb={1}>
                Workers
              </Typography>
              <List sx={{ p: 0 }}>
                {workers.map((worker) => (
                  <ListItemButton
                    key={worker?._id}
                    onClick={() => onWorkerClick(worker)}
                    selected={selectedWorkerId === worker?._id}
                    sx={{ borderRadius: 1, mb: 0.5 }}
                  >
                    <Avatar
                      src={worker?.profileImage || ""}
                      alt={worker?.firstName || "W"}
                      sx={{ width: 30, height: 30, mr: 1 }}
                    />
                    <ListItemText
                      primary={`${worker?.firstName || ""} ${worker?.lastName || ""}`}
                      secondary={worker?.email || ""}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: 500 }}
                      secondaryTypographyProps={{ fontSize: 11 }}
                    />
                  </ListItemButton>
                ))}
              </List>
            </Box>

            <Box
              sx={{
                flex: 1,
                border: "1px solid #ececec",
                borderRadius: 2,
                p: 2,
                minHeight: 420,
              }}
            >
              <Typography fontSize={15} fontWeight={600} mb={1.5}>
                {workerLabel}
              </Typography>

              <Divider sx={{ mb: 2 }} />

              {loading ? (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  minHeight={280}
                >
                  <CircularProgress size={26} />
                </Box>
              ) : !selectedWorkerId ? (
                <Typography fontSize={13} color="text.secondary">
                  Select a worker to view submitted images and comments.
                </Typography>
              ) : (
                <Stack gap={2}>
                  <CommentsSection
                    title="Before"
                    comments={submittedData?.comments?.beforeComment || []}
                  />
                  <ImageCarousel
                    // title="Before"
                    images={submittedData?.images?.beforeWorkImages || []}
                    onPreviewImage={setPreviewImage}
                  />
                  <CommentsSection
                    title="After"
                    comments={submittedData?.comments?.afterComment || []}
                  />
                  <ImageCarousel
                    // title="After"
                    images={submittedData?.images?.afterWorkImages || []}
                    onPreviewImage={setPreviewImage}
                  />
                  {/* <ImageCarousel
                  title="Additional Images"
                  images={submittedData?.images?.additionalImages || []}
                /> */}
                </Stack>
              )}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(previewImage)}
        onClose={() => setPreviewImage("")}
        fullScreen
        PaperProps={{
          sx: {
            bgcolor: "#000",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 1,
          }}
        >
          <IconButton
            onClick={() => setPreviewImage("")}
            sx={{ color: "#fff", bgcolor: "rgba(255,255,255,0.12)" }}
          >
            <X size={20} />
          </IconButton>
        </Box>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Box
            component="img"
            src={previewImage}
            alt="Preview"
            sx={{
              maxWidth: "100%",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
        </Box>
      </Dialog>
    </>
  );
};

export default ViewSubmittedImagesDialog;
